import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBackendHome } from '../hooks/useBackendHome';
import {
  ArrowLeft, ArrowRight, Play, Pause, BookOpen, GraduationCap, Sparkles,
  Lock, ChevronRight, Volume2, Globe, Layers, Crown
} from 'lucide-react';
import { PHONEMES } from '../data/phonemes';
import {
  PHONEME_CATALOGUE as CATALOGUE,
  hasPremiumAccess,
  getIpaForDialect,
  getInventoryTotals,
} from '../data/phonemeCatalogue';
import useDialect from '../hooks/useDialect';
import { usePageMeta } from '../hooks/usePageMeta';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../lib/backend';
import LMSPremiumPaywall from '../components/LMSPremiumPaywall';

/* =========================================================================
 *  PhonemeLibraryPage  —  /lms/phonemes
 *  (catalogue data is now centralised in /data/phonemeCatalogue.js so the
 *   PhonemeCardPage paywall guard can share the same source of truth)
 * ======================================================================= */

const GROUPS = [
  { id: 'vowel',      label: 'Vocali',     color: 'from-cyan-500 to-blue-600',    soft: 'from-cyan-500/10  to-blue-500/5',    border: 'border-cyan-500/30',   accent: 'text-cyan-300' },
  { id: 'diphthong',  label: 'Dittonghi',  color: 'from-orange-500 to-amber-500', soft: 'from-orange-500/10 to-amber-500/5',  border: 'border-orange-500/30', accent: 'text-orange-300' },
  { id: 'consonant',  label: 'Consonanti', color: 'from-violet-500 to-fuchsia-500',soft: 'from-violet-500/10 to-fuchsia-500/5',border: 'border-violet-500/30', accent: 'text-violet-300' },
];


// ------- §DIALECT FACT-CHECK badge (library preview) --------------------
// Rendered on cards whose phoneme is exclusive to one accent (see the
// canonical ``dialectScope`` in phonemeCatalogue.js). Students can spot at
// a glance which sounds only exist in RP or GA without opening the card —
// mirroring the locked toggle inside PhonemeCardPage. Compact pill; keeps
// the library grid uncluttered.
const DialectOnlyBadge = ({ entry }) => {
  const scope = entry?.dialectScope;
  if (!scope || scope === 'both') return null;
  const isRP = scope === 'RP-only';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${
        isRP
          ? 'bg-blue-500/10 border-blue-400/50 text-blue-200'
          : 'bg-red-500/10 border-red-400/50 text-red-200'
      }`}
      title={
        isRP
          ? 'Fonema esclusivo dell\'inglese britannico (RP). Non esiste come suono distinto in American English.'
          : 'Fonema esclusivo dell\'inglese americano (GenAm). Non esiste come suono distinto in British RP.'
      }
      data-testid={`phoneme-library-dialect-badge-${entry?.id}`}
    >
      <span aria-hidden="true">{isRP ? '🇬🇧' : '🇺🇸'}</span>
      <span>{isRP ? 'RP only' : 'US only'}</span>
    </span>
  );
};


// ------- Mini audio player used in published cards -----------------------
const ListenButton = ({ src, label = 'Listen', testId }) => {
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  useEffect(() => () => { if (ref.current) { try { ref.current.pause(); } catch {/*noop*/} } }, []);
  const handle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!src) return;
    if (!ref.current) {
      const url = src.startsWith('http') ? src : `${BACKEND_URL}${src}`;
      ref.current = new Audio(url);
      ref.current.preload = 'metadata';
      ref.current.addEventListener('ended',  () => setPlaying(false));
      ref.current.addEventListener('pause',  () => setPlaying(false));
      ref.current.addEventListener('error',  () => setPlaying(false));
    }
    if (playing) { ref.current.pause(); return; }
    ref.current.currentTime = 0;
    ref.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  return (
    <button
      type="button"
      onClick={handle}
      aria-label={label}
      data-testid={testId}
      className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 ${
        playing
          ? 'bg-orange-500 border-orange-400 text-slate-900 shadow-[0_0_24px_rgba(251,146,60,0.6)] scale-110'
          : 'bg-slate-900/70 border-cyan-500/40 text-cyan-200 hover:border-orange-400 hover:text-orange-300 hover:scale-105'
      }`}
    >
      {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
    </button>
  );
};

// ------- Published mini-card ---------------------------------------------
const PublishedCard = ({ entry, phoneme, group, dialect, index, locked, dialectMismatch = false, onLockedClick, onDialectMismatchClick }) => {
  const isoAudio = phoneme?.audio?.[dialect]?.isolated || phoneme?.audio?.AmE?.isolated;
  const isPremium = entry.access === 'premium';
  // Mono-dialect phonemes (RP-only / GA-only) ALWAYS render the symbol of the
  // dialect they belong to, regardless of the toggle — otherwise the tile would
  // show a cross-dialect symbol (e.g. US /ɛr/) while the badge says "RP only".
  // ipaUS is kept in the data (used by the mono-dialect dialog), just not for
  // the tile symbol here.
  const isMonoDialect = entry.dialectScope === 'RP-only' || entry.dialectScope === 'GA-only';
  const displayIpa = isMonoDialect
    ? (entry.dialectScope === 'GA-only' ? (entry.ipaUS || entry.ipa) : entry.ipa)
    : getIpaForDialect(entry, dialect);
  const altIpa = (dialect === 'AmE' && entry.ipa !== displayIpa) ? entry.ipa
                : (dialect === 'RP'  && entry.ipaUS && entry.ipaUS !== displayIpa) ? entry.ipaUS
                : null;

  const handleClick = (e) => {
    if (dialectMismatch) {
      // Educational side-quest: instead of silently blocking the visitor,
      // pop the "how this phoneme is realised in your accent" dialog so
      // the mismatch becomes a teaching moment.
      e.preventDefault();
      e.stopPropagation();
      onDialectMismatchClick?.(entry);
      return;
    }
    if (locked) {
      e.preventDefault();
      e.stopPropagation();
      onLockedClick?.(entry);
    }
  };

  return (
    <Link
      to={(locked || dialectMismatch) ? '#' : `/lms/phoneme/${entry.id}`}
      onClick={handleClick}
      className={`group relative block ${locked || dialectMismatch ? 'cursor-pointer' : ''}`}
      data-testid={`phoneme-library-card-${entry.id}`}
      data-locked={locked ? 'true' : 'false'}
      data-dialect-mismatch={dialectMismatch ? 'true' : 'false'}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div
        className={`relative h-full rounded-3xl border ${
          locked ? 'border-amber-500/40' : group.border
        } bg-gradient-to-br ${group.soft} backdrop-blur-sm p-6 overflow-hidden transition-all duration-500 group-hover:-translate-y-1.5 ${
          locked ? 'group-hover:border-orange-400/80' : 'group-hover:border-orange-400/60'
        }`}
      >
        {/* Hover glow */}
        <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
             style={{ background: 'radial-gradient(circle at 30% 0%, rgba(251,146,60,0.18) 0%, transparent 60%)' }} />

        {/* Top row: status pill (reflects open/locked/mono-dialect) + access label */}
        <div className="flex items-start justify-between gap-3 relative z-10">
          {dialectMismatch ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
              mono-dialetto · {group.label}
            </span>
          ) : locked ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300/80">
              <Lock className="w-3 h-3" /> {group.label}
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold ${group.accent}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
              disponibile · {group.label}
            </span>
          )}
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <DialectOnlyBadge entry={entry} />
            {isPremium ? (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                locked
                  ? 'bg-amber-500/20 border border-amber-400/60 text-amber-200'
                  : 'bg-amber-500/10 border border-amber-500/40 text-amber-300'
              }`}
              data-testid="phoneme-library-badge-premium"
            >
              {locked ? <Lock className="w-3 h-3" /> : <Crown className="w-3 h-3" />}
              Premium
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold uppercase tracking-wider"
              data-testid="phoneme-library-badge-free"
            >
              <Sparkles className="w-3 h-3" />
              Gratis
            </span>
          )}
          </div>
        </div>

        {/* IPA + lexical set */}
        <div className="mt-6 mb-4 relative z-10">
          <p className="text-5xl md:text-6xl font-black text-white leading-none tracking-tight">
            /{displayIpa}/
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.22em] text-slate-400 font-semibold">
            {entry.subtitle}
            {(entry.subgroup || phoneme?.subcategory) && (
              <span className="ml-2 text-slate-500 normal-case tracking-normal text-[11px]">
                · {(entry.subgroup || phoneme?.subcategory).replace(/-/g, ' ')}
              </span>
            )}
          </p>
          {altIpa && (
            <p className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">
              {dialect === 'AmE' ? 'RP' : 'US'}: <span className="font-mono text-slate-300">/{altIpa}/</span>
            </p>
          )}
        </div>

        {/* Example words */}
        <div className="flex flex-wrap gap-1.5 mb-5 relative z-10">
          {(entry.words || []).map((w, i) => (
            <span key={i} className="px-2.5 py-1 rounded-full bg-slate-900/60 border border-slate-700 text-xs text-slate-300 group-hover:border-cyan-500/40 transition-colors">
              {w}
            </span>
          ))}
        </div>

        {/* Footer row */}
        <div className="flex items-center justify-between gap-3 relative z-10">
          <span className="text-xs text-slate-400">
            {phoneme
              ? `${phoneme.commonWords?.length || 0} parole · ${phoneme.exampleSentences?.length || 0} frasi`
              : 'Scheda premium completa'}
          </span>
          {locked ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300">
              <Lock className="w-3.5 h-3.5" />
              Sblocca
            </span>
          ) : dialectMismatch ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400">
              <Globe className="w-3.5 h-3.5" />
              Dettaglio dialetto
            </span>
          ) : (phoneme && isoAudio) ? (
            <ListenButton
              src={isoAudio}
              label={`Ascolta /${phoneme.ipa}/ ${dialect === 'AmE' ? 'in americano' : 'in britannico'}`}
              testId={`phoneme-library-listen-${entry.id}`}
            />
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300">
              Apri
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// ------- Global dialect toggle (US ⇄ UK) ---------------------------------
const DialectToggle = ({ dialect, onChange }) => (
  <div
    className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-900/70 border border-cyan-500/30 backdrop-blur-md shadow-lg"
    data-testid="library-dialect-toggle"
    role="tablist"
    aria-label="Variante di pronuncia"
  >
    {[
      { id: 'AmE', label: 'US', flag: '🇺🇸', sub: 'American English' },
      { id: 'RP',  label: 'UK', flag: '🇬🇧', sub: 'Received Pronunciation' },
    ].map((opt) => {
      const active = dialect === opt.id;
      return (
        <button
          key={opt.id}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(opt.id)}
          data-testid={`library-dialect-${opt.id === 'AmE' ? 'us' : 'uk'}`}
          className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
            active
              ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-slate-900 shadow-[0_0_20px_rgba(251,146,60,0.45)]'
              : 'text-slate-300 hover:text-white'
          }`}
        >
          <span className="text-base leading-none">{opt.flag}</span>
          <span className="leading-none">{opt.label}</span>
          <span className={`hidden md:inline text-[10px] uppercase tracking-widest font-semibold ${active ? 'opacity-80' : 'opacity-50'}`}>
            {opt.sub}
          </span>
        </button>
      );
    })}
  </div>
);

// =========================================================================
const PhonemeLibraryPage = () => {
  const backendHome = useBackendHome();
  usePageMeta({ title: 'Libreria fonetica inglese: tutti i suoni RP e US — Vocal Fitness®', canonicalPath: '/lms/phonemes' });
  const { dialect, setDialect } = useDialect();
  const { user } = useAuth();
  const [groupFilter, setGroupFilter] = useState('all');
  const [paywallEntry, setPaywallEntry] = useState(null);
  // Dialect-mismatch educational dialog (see handleDialectMismatch below)
  const [dialectDialogEntry, setDialectDialogEntry] = useState(null);

  // Fetch published cards from the CMS so newly-published phonemes appear here
  // without needing to hand-edit the static PHONEMES map / catalogue status.
  const [dbPhonemes, setDbPhonemes] = useState({});   // {id: cardObj}
  const [dbLoaded, setDbLoaded] = useState(false);

  const isPremium = hasPremiumAccess(user);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/phonemes`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        if (cancelled) return;
        const byId = {};
        for (const c of list) byId[c.id] = c;
        setDbPhonemes(byId);
      } catch (e) {
        // Fail silently — fall back to static PHONEMES entries
        console.warn('[PhonemeLibraryPage] failed to fetch DB phonemes:', e.message);
      } finally {
        if (!cancelled) setDbLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => getInventoryTotals(), []);

  // Published-card counts — SINGLE SOURCE OF TRUTH = the /api/phonemes payload
  // (published:true). Both the header stats and the paywall promise derive
  // from these numbers, so they can never contradict each other. No static
  // fallback: an honest 0 in preview beats a fake catalogue number.
  const pub = useMemo(() => {
    const cards = Object.values(dbPhonemes);
    const byCat = (c) => cards.filter((x) => (x.category || '').toLowerCase() === c).length;
    return {
      total: cards.length,
      vowel: byCat('vowel'),
      diphthong: byCat('diphthong'),
      consonant: byCat('consonant'),
    };
  }, [dbPhonemes]);

  // Merge static catalogue with dynamic DB state: any card with a matching
  // DB entry is treated as `status:'published'`, overriding the static value.
  const filtered = useMemo(() => {
    const base = groupFilter === 'all' ? CATALOGUE : CATALOGUE.filter(e => e.group === groupFilter);
    return base.map(e => (dbPhonemes[e.id] ? { ...e, status: 'published' } : e));
  }, [groupFilter, dbPhonemes]);

  const handleLockedClick = (entry) => setPaywallEntry(entry);
  const handleDialectMismatch = (entry) => setDialectDialogEntry(entry);

  // Suggested US-equivalent card when clicking a grey-outed RP-only phoneme.
  // Mapping is canonical: each RP-exclusive phoneme has a documented US
  // realisation via a different (or rhotic-adjacent) phoneme.
  const DIALECT_REDIRECTS = {
    'o-lot':     { toId: 'a-palm',  toIpa: '/ɑ/',  toLabel: 'PALM' },
    'ie-near':   { toId: 'i-kit',   toIpa: '/ɪr/', toLabel: 'KIT + /r/' },
    'ee-square': { toId: 'e-dress', toIpa: '/ɛr/', toLabel: 'DRESS + /r/' },
    'ue-cure':   { toId: 'u-foot',  toIpa: '/ʊr/', toLabel: 'FOOT + /r/' },
  };

  return (
    <div className="min-h-screen bg-[#04070d] text-slate-200" data-testid="phoneme-library-page">
      <style>{`
        @keyframes lib-fade-up { from { opacity:0; transform: translateY(18px); } to { opacity:1; transform:none; } }
        .lib-card-enter      { animation: lib-fade-up .6s ease-out both; }
        @keyframes lib-pulse-aura {
          0%,100% { box-shadow: 0 0 0 0 rgba(251,146,60,0); }
          50%     { box-shadow: 0 0 48px 0 rgba(251,146,60,0.22); }
        }
        .lib-aura { animation: lib-pulse-aura 5s ease-in-out infinite; }
        /* §DIALECT FACT-CHECK — muted state for cards whose phoneme
           only exists in the OTHER accent than the one the user has
           currently selected. Content stays visible for educational
           context; hover/click and audio are disabled downstream.
           !important overrides the final state of the lib-fade-up
           animation (which sets opacity:1). */
        .lib-card-dialect-mute            { opacity: 0.4 !important; filter: saturate(0.3) grayscale(0.35); }
        .lib-card-dialect-mute:hover      { opacity: 0.75 !important; filter: saturate(0.6) grayscale(0.15); transition: opacity .3s ease, filter .3s ease; cursor: help; }
        /* Do NOT disable pointer events — we want clicks to open the
           educational dialog. The click handler in <PublishedCard>
           routes to onDialectMismatchClick instead of navigating. */
      `}</style>

      {/* Ambient drifting blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-0">
        <div className="absolute top-[8%]  left-[6%]  w-96 h-96 bg-cyan-500/10  rounded-full blur-3xl" />
        <div className="absolute top-[40%] right-[4%] w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0  left-[35%] w-[28rem] h-[28rem] bg-violet-500/10 rounded-full blur-3xl" />
      </div>

      {/* ---------- Sticky header ---------- */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#04070d]/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to={backendHome.path} className="flex items-center gap-3 text-cyan-300 hover:text-white transition-colors" data-testid="library-home-link" title={backendHome.label}>
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide">Vocal Fitness LMS</span>
          </Link>
          <DialectToggle dialect={dialect} onChange={setDialect} />
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs uppercase tracking-[0.22em] font-bold mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Phoneme Card Library
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-[1.05] text-white mb-6">
            La biblioteca interattiva<br className="hidden md:block" />
            <span className="bg-gradient-to-r from-cyan-300 via-orange-300 to-violet-300 bg-clip-text text-transparent">
              dei fonemi inglesi.
            </span>
          </h1>
          <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed mb-9">
            Ogni card è uno studio articolatorio completo — sezione sagittale, hotspot anatomici, esempi audio nella voce del Prof. Steve Dapper, mnemonici e laboratorio Pink Trombone. Scegli la variante che ti interessa e inizia.
          </p>

          {/* Stats — TUTTI dinamici (fonte unica: /api/phonemes published).
              Le card complete e la loro scomposizione per categoria vengono
              dallo stesso payload → nessuna riga mescola dinamico e statico. */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10" data-testid="library-stats">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-black text-emerald-300" data-testid="library-stat-complete">{pub.total}</p>
              <p className="text-[10px] uppercase tracking-widest text-emerald-200/70 font-bold">schede complete</p>
            </div>
            <div className="w-px h-10 bg-slate-800" />
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-black text-cyan-300" data-testid="library-stat-vowels">{pub.vowel}</p>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300/70 font-bold">vocali</p>
            </div>
            <div className="w-px h-10 bg-slate-800" />
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-black text-orange-300" data-testid="library-stat-diphthongs">{pub.diphthong}</p>
              <p className="text-[10px] uppercase tracking-widest text-orange-200/70 font-bold">dittonghi</p>
            </div>
            <div className="w-px h-10 bg-slate-800" />
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-black text-violet-300" data-testid="library-stat-consonants">{pub.consonant}</p>
              <p className="text-[10px] uppercase tracking-widest text-violet-200/70 font-bold">consonanti</p>
            </div>
            <div className="w-px h-10 bg-slate-800" />
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-black text-white flex items-center justify-center gap-2">
                <Globe className="w-6 h-6" />
                UK + US
              </p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">varianti incluse</p>
            </div>
          </div>

          <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-slate-500" data-testid="library-inventory-note">
            <span className="text-slate-400 font-bold">Inventario sistema fonetico</span> · {stats.rp} RP · {stats.ga} US
            <span className="ml-2 normal-case tracking-normal text-slate-600">(fonemi totali della lingua — distinto dalle card complete acquistabili qui sopra)</span>
          </p>
        </div>
      </section>

      {/* ---------- Group filter chips ---------- */}
      <section className="max-w-7xl mx-auto px-6 mb-8">
        <div className="flex flex-wrap items-center gap-2" data-testid="library-group-filter">
          {[{ id: 'all', label: 'Tutti', count: CATALOGUE.length, accent: 'text-white' }, ...GROUPS.map(g => ({
            id: g.id, label: g.label, count: CATALOGUE.filter(e => e.group === g.id).length, accent: g.accent
          }))].map((g) => {
            const active = groupFilter === g.id;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setGroupFilter(g.id)}
                data-testid={`library-filter-${g.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 border ${
                  active
                    ? 'bg-white text-slate-900 border-white shadow-lg scale-105'
                    : `bg-slate-900/50 border-slate-700 ${g.accent} hover:border-slate-500`
                }`}
              >
                <Layers className={`w-3.5 h-3.5 ${active ? 'text-slate-900' : 'opacity-70'}`} />
                {g.label}
                <span className={`text-[10px] font-bold ${active ? 'text-slate-500' : 'opacity-50'}`}>{g.count}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ---------- Dialect inventory micro-stats ---------- */}
      {(() => {
        const rpOnly = CATALOGUE.filter((e) => e.dialectScope === 'RP-only').length;
        const gaOnly = CATALOGUE.filter((e) => e.dialectScope === 'GA-only').length;
        const bothScope = CATALOGUE.length - rpOnly - gaOnly;
        const rpInventory = bothScope + rpOnly;
        const gaInventory = bothScope + gaOnly;
        const muted = dialect === 'AmE' ? rpOnly : gaOnly;
        const activeLabel = dialect === 'AmE' ? 'US GenAm' : 'UK RP';
        const otherLabel  = dialect === 'AmE' ? 'RP britannico' : 'US GenAm';
        if (rpOnly + gaOnly === 0) return null; // no mono-dialect phonemes → nothing to say
        return (
          <section className="max-w-7xl mx-auto px-6 mb-6" data-testid="library-dialect-stats">
            <div className="rounded-2xl border border-cyan-500/25 bg-slate-900/50 backdrop-blur-sm px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none">{dialect === 'AmE' ? '🇺🇸' : '🇬🇧'}</span>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-cyan-300/70 font-bold">Inventario attivo</p>
                  <p className="text-white font-black text-lg leading-tight">
                    {dialect === 'AmE' ? gaInventory : rpInventory}
                    <span className="text-cyan-300/70 font-normal text-sm ml-1">fonemi · {activeLabel}</span>
                  </p>
                </div>
              </div>
              <div className="h-8 w-px bg-cyan-500/20 hidden sm:block" />
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none opacity-40">{dialect === 'AmE' ? '🇬🇧' : '🇺🇸'}</span>
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Alternativa</p>
                  <p className="text-slate-300 font-bold text-lg leading-tight">
                    {dialect === 'AmE' ? rpInventory : gaInventory}
                    <span className="text-slate-500 font-normal text-sm ml-1">fonemi · {otherLabel}</span>
                  </p>
                </div>
              </div>
              <div className="h-8 w-px bg-cyan-500/20 hidden sm:block" />
              <div className="flex-1 min-w-[260px]">
                <p className="text-[9px] uppercase tracking-widest text-orange-300 font-bold">Mono-dialetto</p>
                <p className="text-white text-sm">
                  {muted > 0
                    ? <><b className="text-orange-300 font-black">{muted}</b> fonem{muted === 1 ? 'a' : 'i'} {dialect === 'AmE' ? 'RP-only' : 'GA-only'} — grigiati in questo accento perché non esistono in {activeLabel}.</>
                    : <>Nessun fonema mono-dialetto attivo in {activeLabel} — tutte le card sono compatibili.</>}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Cambia il toggle US⇄UK in alto a destra per riattivarli e vedere i loro equivalenti nell&rsquo;altro accento.
                </p>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ---------- Catalogue grid ---------- */}
      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="library-grid">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((entry, i) => {
            const group = GROUPS.find(g => g.id === entry.group) || GROUPS[0];
            // §DIALECT FACT-CHECK — grey-out cards that only exist in the
            // OTHER accent (mono-dialect). Kept visible + clickable to open
            // the educational mono-dialect dialog.
            const scope = entry.dialectScope;
            const dialectMismatch = (
              (scope === 'RP-only' && dialect === 'AmE') ||
              (scope === 'GA-only' && dialect === 'RP')
            );
            const wrapperCls = `lib-card-enter h-full ${dialectMismatch ? 'lib-card-dialect-mute' : ''}`;

            // CLIENT VIEW — only two real states (plus mono-dialect):
            //   OPEN   → free card, OR published in the DB and the user has
            //            premium access → navigates to the full card.
            //   LOCKED → everything else → click opens the paywall.
            // There is NO "in preparazione" state: an unpublished card simply
            // behaves as a premium-locked tile. The "N card complete" counter
            // (from /api/phonemes) is the single source for what's openable.
            const isFree = entry.access !== 'premium';
            const publishedInDb = !!dbPhonemes[entry.id];
            const openable = !dialectMismatch && (isFree || (publishedInDb && isPremium));
            const locked = !openable && !dialectMismatch;
            const phoneme = dbPhonemes[entry.id] || PHONEMES[entry.id] || null;

            return (
              <div
                key={entry.id}
                className={wrapperCls}
                style={{ animationDelay: `${i * 50}ms` }}
                data-dialect-mismatch={dialectMismatch ? 'true' : 'false'}
              >
                <PublishedCard
                  entry={entry}
                  phoneme={phoneme}
                  group={group}
                  dialect={dialect}
                  index={i}
                  locked={locked}
                  dialectMismatch={dialectMismatch}
                  onLockedClick={handleLockedClick}
                  onDialectMismatchClick={handleDialectMismatch}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------- Recommended starting point ---------- */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="relative rounded-3xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 via-amber-500/5 to-slate-900/20 p-8 md:p-10 overflow-hidden lib-aura">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 100% 0%, rgba(251,146,60,0.18) 0%, transparent 50%)' }} />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="text-orange-300 text-xs uppercase tracking-[0.22em] font-bold mb-3 flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Da dove iniziare
              </p>
              <h2 className="text-2xl md:text-3xl font-black text-white leading-tight mb-3">
                Prima volta? Inizia da <span className="text-orange-300">/ʊ/ FOOT</span>.
              </h2>
              <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                È la vocale meno frequente dell&rsquo;inglese, motivo per cui paradossalmente padroneggiarla diventa un differenziale fortissimo nella performance parlata. Card completa con 30 parole, 9 hotspot anatomici, Pink Trombone live.
              </p>
            </div>
            <Link
              to="/lms/phoneme/u-foot"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-slate-900 font-bold shadow-[0_10px_30px_rgba(251,146,60,0.45)] hover:scale-105 transition-transform duration-300"
              data-testid="library-cta-u-foot"
            >
              <Volume2 className="w-5 h-5" />
              Apri /ʊ/ FOOT
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p className="flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" />
            Vocal Fitness LMS · Phoneme Library
          </p>
          <p>
            Variante corrente: <span className="text-cyan-300 font-bold">{dialect === 'AmE' ? 'US (American English)' : 'UK (Received Pronunciation)'}</span>
          </p>
        </div>
      </footer>
      {/* ---------- Premium paywall (modal) ---------- */}
      {paywallEntry && (
        <LMSPremiumPaywall
          variant="modal"
          cardId={paywallEntry.id}
          cardLabel={`/${(PHONEMES[paywallEntry.id]?.ipa) || paywallEntry.ipa || ''}/ ${paywallEntry.subtitle}`}
          onClose={() => setPaywallEntry(null)}
        />
      )}
      {/* ---------- Dialect-mismatch educational dialog ---------- */}
      {dialectDialogEntry && (() => {
        const e = dialectDialogEntry;
        const redirect = DIALECT_REDIRECTS[e.id];
        const isRP = e.dialectScope === 'RP-only';
        const activeAccent = isRP ? 'RP britannico' : 'American English';
        const otherAccent  = isRP ? 'American English' : 'RP britannico';
        return (
          <div
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDialectDialogEntry(null)}
            data-testid="phoneme-library-dialect-dialog"
          >
            <div
              className="relative max-w-lg w-full bg-slate-900 border border-cyan-500/40 rounded-2xl p-6 shadow-2xl"
              onClick={(ev) => ev.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setDialectDialogEntry(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 flex items-center justify-center transition-colors"
                aria-label="Chiudi"
                data-testid="phoneme-library-dialect-dialog-close"
              >×</button>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl leading-none">{isRP ? '🇬🇧' : '🇺🇸'}</span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">
                    fonema mono-dialetto
                  </p>
                  <h3 className="text-xl font-black text-white">
                    /{e.ipa}/ · {e.subtitle}
                  </h3>
                </div>
              </div>
              <p className="text-cyan-100/85 text-sm leading-relaxed mb-4">
                Il fonema <b className="text-white">/{e.ipa}/</b> esiste solo in <b>{activeAccent}</b>. In <b>{otherAccent}</b> {isRP
                  ? <>si è fuso con un altro suono oppure viene sostituito da un fonema rotico</>
                  : <>non esiste come suono distinto</>}. Sentirlo pronunciato nell&rsquo;accento sbagliato confonderebbe le tue orecchie: sarebbe un fonema diverso.
              </p>
              {redirect ? (
                <div className="bg-slate-950/60 border border-cyan-500/30 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-cyan-300/70 font-bold mb-2">
                    equivalente in {otherAccent}
                  </p>
                  <p className="text-cyan-50 text-sm mb-3">
                    In {otherAccent.split(' ')[0]}, parole come{' '}
                    <span className="font-semibold text-white">{(e.words || []).slice(0,2).join(', ') || '…'}</span>{' '}
                    si pronunciano con il fonema{' '}
                    <b className="font-mono text-orange-300">{redirect.toIpa}</b>{' '}
                    <span className="text-slate-400">({redirect.toLabel})</span>.
                  </p>
                  <Link
                    to={`/lms/phoneme/${redirect.toId}`}
                    onClick={() => setDialectDialogEntry(null)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-slate-900 font-bold text-sm hover:scale-[1.02] transition-transform"
                    data-testid="phoneme-library-dialect-dialog-cta"
                  >
                    Esplora /{redirect.toIpa.replace(/\//g,'')}/ {redirect.toLabel}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <p className="text-slate-400 text-xs italic">
                  Consiglio: cambia il toggle US⇄UK nell&rsquo;header per riattivare questa scheda.
                </p>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default PhonemeLibraryPage;
